import type { CSSProperties } from "react";

import { cn } from "./cn";
import { SparkChart } from "./spark";
import "./stat-tile.css";

export interface StatTileProps {
  label: string;
  /** PRE-FORMATTED. The component does not format numbers itself — pick
   *  formatCompactCount / formatUsdTotal (chart-format.ts) or your own percent/
   *  duration formatter at the call site. */
  value: string;
  sub?: string;
  /** Resolved accent color for the leading bar + spark (a slot from
   *  useCategoricalColors, or a status token). Omit for a neutral tile with no
   *  entity behind it. */
  accent?: string;
  spark?: number[];
  /** Whether THIS render wears the danger treatment. Not gated on the raw
   *  number: pass the resolved condition (e.g. `danger={rawValue > 0}`) so the
   *  component stays a pure render. */
  danger?: boolean;
  /** Opens a drilldown behind this number. When present the tile renders as a
   *  real, keyboard-reachable <button>; otherwise an inert <div>. Identical
   *  markup either way so a grid of tiles reads as one system. */
  onOpen?: () => void;
  /** Accessible hint + title for the clickable variant. Defaults to a plain
   *  phrase built from `label`; override for domain-specific wording. */
  openTitle?: string;
  className?: string;
}

/**
 * The stat-tile primitive: a label, a big value, an optional subline, an
 * optional entity-colored leading bar, and an optional sparkline. The value
 * always wears ink (never the accent color) — the colored mark beside it
 * carries identity, per the house dataviz rule.
 */
export function StatTile({
  label,
  value,
  sub,
  accent,
  spark,
  danger,
  onOpen,
  openTitle,
  className,
}: StatTileProps) {
  const style = accent ? ({ "--rc-chart-accent": accent } as CSSProperties) : undefined;
  const classes = cn("rc-stat-tile", danger && "rc-stat-tile--danger", className);
  const body = (
    <>
      {accent ? <span className="rc-stat-tile__bar" aria-hidden="true" /> : null}
      <span className="rc-stat-tile__label">{label}</span>
      <span className="rc-stat-tile__value">{value}</span>
      {sub ? <span className="rc-stat-tile__sub">{sub}</span> : null}
      {spark && accent ? <SparkChart values={spark} color={accent} /> : null}
    </>
  );
  if (onOpen) {
    return (
      <button
        type="button"
        className={classes}
        style={style}
        onClick={onOpen}
        title={openTitle ?? `See the rows behind ${label.toLowerCase()}`}
      >
        {body}
      </button>
    );
  }
  return (
    <div className={classes} style={style}>
      {body}
    </div>
  );
}
